import { mapActions } from 'pinia';
import constants from '@/js/constants.js';
import usePlanningElementsStore from '@/stores/planning/elements';
import useSelectionStore from '@/stores/selection';
import useUiPlanningStore from '@/stores/ui/planning';

/** ***************************** */
/** *     ELEMENTS RESIZE      ** */
/** ***************************** */
function cleanResizePlaceholders(planning) {
  planning.elements.filter(item => item.isResizePlaceholder).forEach((resizePlaceholder) => {
    resizePlaceholder.isResizePlaceholder = false;
  });
}

let lastResize = 0;
let resizeThrottledTimeout;

function doResize(el, elWidth, handle) {
  lastResize = Date.now();
  el.resize(elWidth, handle);
}

const resizeThrottled = _.throttle((el, elWidth, handle) => {
  if (resizeThrottledTimeout) clearTimeout(resizeThrottledTimeout);
  if (Date.now() - lastResize < 3000) {
    resizeThrottledTimeout = setTimeout(() => doResize(el, elWidth, handle));
    return;
  }
  doResize(el, elWidth, handle);
}, 100);

export default {
  data() {
    return {
      selectionStore: useSelectionStore(),
      uiPlanningStore: useUiPlanningStore(),
    };
  },
  methods: {
    resizableConfig(el) {
      if (! this.isDynamic(el)) return { enabled: false };
      const vm = this;
      return {
        enabled: ! (el.isType('macro') || (el.isType('task') && el.getIsLocked()) || (this.planning.config.lockDragAndDrop && this.planning.meta.access_right != 'admin')),
        edges: { left: true, right: true, bottom: false, top: false },
        margin: 5, // handle size
        ignoreFrom: '.element-menu',
        restrictSize: {
          min: { width: el.isType('milestone') ? constants.milestoneMinWidth : constants.taskMinWidth, height: 0 },
        },
        listeners: {
          start(event) {
            vm.onResizeStart(event, el);
          },
          move(event) {
            vm.onResizeMove(event, el);
          },
          end(event) {
            vm.onResizeEnd(event, el);
          },
        },
      };
    },

    onResizeStart(event, el) {
      event.target.dragData = { // store data in event.target
        id: el.id,
        laneXOffset: document.getElementById(`lane${el.getLaneId()}`)?.getBoundingClientRect().left || 0, // to calculate position in lane
        position: { left: event.pageX, top: event.pageY }, // current position in page
        currentWidth: event.target.offsetWidth,
        handle: event.edges.left ? 'w' : 'e',
      };
      this.uiPlanningStore.doNotDisplayElementMenu = true;
      setTimeout(() => { event.target.classList.add('is-resizing'); });
      this.startChangingElement();
      this.selectionStore.reset([el.id]);

      cleanResizePlaceholders(this.planning);
      el.isResizePlaceholder = true;

      if (el.isType('task')) this.setSnapBoundaries(event.target);
    },

    onResizeMove(event, el) {
      event.target.dragData.position.left = event.pageX;
      event.target.dragData.currentWidth = event.rect.width;
      if (el.isType('task')) this.snapToBoundaries(event.target);
      event.target.dragData.currentWidth = Math.max(event.target.dragData.currentWidth, el.isType('milestone') ? constants.milestoneMinWidth : constants.taskMinWidth);
      resizeThrottled(el, event.target.dragData.currentWidth, event.target.dragData.handle);
    },

    onResizeEnd(event, el) {
      this.uiPlanningStore.doNotDisplayElementMenu = false;
      setTimeout(() => { event.target.classList.remove('is-resizing'); }, 600);
      cleanResizePlaceholders(this.planning);
      event.target.dragData.currentWidth = event.rect.width;
      event.target.dragData.currentWidth = Math.max(event.target.dragData.currentWidth, el.isType('milestone') ? constants.milestoneMinWidth : constants.taskMinWidth);
      el.resize(event.target.dragData.currentWidth, event.target.dragData.handle);
      this.changingElement(el);
    },

    /** KANBAN */
    kanbanResizableConfig() {
      return { enabled: false, handles: {} };
    },

    ...mapActions(usePlanningElementsStore, ['setSnapBoundaries', 'snapToBoundaries', 'changingElement']),
  },
};
